import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import Loader from '../../../components/common/Loader';
import { GLOBAL_DEFAULT } from '../../../constants/global';
import { orderService } from '../services';
import { applyOrdersArchiveRecipientWarehouse, toOrdersArray } from './thermal/orderPrintNormalize';
import PaginatedPackingSlipOrder from './thermal/PaginatedPackingSlipOrder';
import './thermal/printPackingSlipPeel.scss';
import { useThermalPrintViewSetup } from './thermal/useThermalPrintViewSetup';

/** PNG URLs for carrier label(s), one sheet per URL when printing. */
function getCarrierLabelPngUrls(order: any): string[] {
    if (!order) {
        return [];
    }
    if (order.is_shipping_label && order?.shipping?.packages?.length) {
        return order.shipping.packages
            .map((packageItem: any) => packageItem?.label_download?.png)
            .filter(Boolean);
    }
    const single = order?.shipping?.label_download?.png;
    return single ? [single] : [];
}

const PrintShippingLabel: React.FC<any> = () => {
    const [state, setState] = useState<any>(null);
    const [orderDetail, setOrderDetail] = useState<any>();
    const [loading, setLoading] = useState<boolean>(GLOBAL_DEFAULT.LOADER_STATE);

    useEffect(() => {
        try {
            const windowName = window.name;
            const state = JSON.parse(windowName);

            setState(state);
        } catch (error) {
            console.error('Failed to parse state from window name', error);
            window.location.href = '/app/dashboard';
        }
    }, []);

    useThermalPrintViewSetup();

    const getOrderDetail = useCallback(async () => {
        if (state?.orderId) {
            setLoading(true);
            try {
                const payload = {
                    orderIds: state?.orderId,
                    type: state?.orderType || 'Orders',
                };
                const response = await orderService.printPackingSlip(payload);

                if (state?.orderType === 'OrdersArchive') {
                    applyOrdersArchiveRecipientWarehouse(response?.data);
                }

                const raw = response?.data;
                if (raw == null) {
                    setOrderDetail(undefined);
                    setLoading(false);
                    return;
                }

                const list = Array.isArray(raw) ? raw : [raw];
                const enriched = list.map((order: any) => ({
                    ...order,
                    is_shipping_label: order?.shipping?.packages?.some(
                        (packageItem: any) => packageItem?.label_download?.png
                    ),
                }));

                setOrderDetail(Array.isArray(raw) ? enriched : enriched[0]);
                setLoading(false);
            } catch (error) {
                setLoading(false);
            }
        }
    }, [state?.orderId, state?.orderType]);

    useEffect(() => {
        getOrderDetail();
    }, [getOrderDetail]);

    const orders = toOrdersArray(orderDetail);

    return (
        <>
            <Loader isLoading={loading} />
            <div className="text-center hide-on-print py-3">
                <Button
                    className="custom-min-width-85"
                    size="sm"
                    variant="dark"
                    onClick={() => window.print()}
                >
                    Print
                </Button>
            </div>
            {orders.map((order: any, index: number) => {
                const labelUrls = getCarrierLabelPngUrls(order);
                const orderKey = order?.order_number ?? `o-${index}`;
                return (
                    <React.Fragment key={orderKey}>
                        {labelUrls.map((src, labelIndex) => (
                            <div
                                key={`${orderKey}-carrier-${labelIndex}`}
                                className="packing-slip-peel-page packing-slip-peel-page--carrier"
                                style={{ lineHeight: 1.2 }}
                            >
                                <div className="packing-slip-carrier-inner">
                                    <img
                                        src={src}
                                        alt="Shipping Label"
                                        className="packing-slip-carrier-img"
                                    />
                                </div>
                            </div>
                        ))}
                        <PaginatedPackingSlipOrder order={order} />
                    </React.Fragment>
                );
            })}
        </>
    );
};

export default PrintShippingLabel;
